import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Bug, Hammer, Link2, RefreshCw, Search, ShieldAlert, Wand2 } from "lucide-react";
import { listErrorReports, updateReportTriage, symbolicateReport } from "@/lib/diagnostics.functions";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { formatDate } from "@/lib/date-format";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/diagnostics")({
  head: () => ({
    meta: [
      { title: "Diagnostica — Lumen" },
      { name: "description", content: "Registro interno degli errori di build e di runtime con stack trace." },
      { property: "og:title", content: "Diagnostica — Lumen" },
      { property: "og:description", content: "Registro interno degli errori di build e di runtime." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DiagnosticsPage,
});

type Kind = "all" | "build" | "runtime";
type Status = "all" | "new" | "in_progress" | "resolved";
type RoleFilter = "all" | "student" | "teacher" | "parent";

const severityTone: Record<string, string> = {
  info: "bg-muted text-muted-foreground",
  warning: "bg-amber-500/15 text-amber-600",
  error: "bg-destructive/15 text-destructive",
  fatal: "bg-destructive text-destructive-foreground",
};

const statusLabel: Record<string, string> = {
  new: "Nuovo",
  in_progress: "In lavorazione",
  resolved: "Risolto",
};

const statusTone: Record<string, string> = {
  new: "bg-destructive/15 text-destructive",
  in_progress: "bg-amber-500/15 text-amber-600",
  resolved: "bg-emerald-500/15 text-emerald-600",
};

function DiagnosticsPage() {
  const [kind, setKind] = useState<Kind>("all");
  const [status, setStatus] = useState<Status>("all");
  const [role, setRole] = useState<RoleFilter>("all");
  const [branch, setBranch] = useState("all");
  const [commit, setCommit] = useState("");
  const [search, setSearch] = useState("");
  const [correlationId, setCorrelationId] = useState("");

  const fetchReports = useServerFn(listErrorReports);

  const filters = {
    kind,
    status,
    role,
    branch: branch === "all" ? undefined : branch,
    commit: commit.trim() || undefined,
    correlationId: correlationId.trim() || undefined,
    search: search.trim() || undefined,
    limit: 100,
  };

  const q = useQuery({
    queryKey: ["diagnostics", filters],
    queryFn: () => fetchReports({ data: filters }),
    refetchInterval: 60_000,
  });

  const resetFilters = () => {
    setKind("all");
    setStatus("all");
    setRole("all");
    setBranch("all");
    setCommit("");
    setSearch("");
    setCorrelationId("");
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4">
      <PageHeader
        icon={<Bug className="size-5" />}
        title="Diagnostica"
        subtitle="Errori di build e di runtime, con stack trace, commit e stato di triage."
      />

      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Tabs value={kind} onValueChange={(v) => setKind(v as Kind)}>
            <TabsList>
              <TabsTrigger value="all">Tutti</TabsTrigger>
              <TabsTrigger value="build">Build</TabsTrigger>
              <TabsTrigger value="runtime">Runtime</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Azzera filtri
            </Button>
            <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
              <RefreshCw className={`mr-2 size-4 ${q.isFetching ? "animate-spin" : ""}`} />
              Aggiorna
            </Button>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Cerca nel messaggio o nel file…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Input placeholder="Commit (anche parziale)" value={commit} onChange={(e) => setCommit(e.target.value)} />
          <Input
            placeholder="Correlation ID sessione"
            value={correlationId}
            onChange={(e) => setCorrelationId(e.target.value)}
          />

          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger>
              <SelectValue placeholder="Branch" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i branch</SelectItem>
              {(q.data?.branches ?? []).map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={role} onValueChange={(v) => setRole(v as RoleFilter)}>
            <SelectTrigger>
              <SelectValue placeholder="Ruolo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              <SelectItem value="student">Studente</SelectItem>
              <SelectItem value="teacher">Docente</SelectItem>
              <SelectItem value="parent">Genitore</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
            <SelectTrigger>
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="new">Nuovo</SelectItem>
              <SelectItem value="in_progress">In lavorazione</SelectItem>
              <SelectItem value="resolved">Risolto</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {q.isLoading ? (
        <LoadingState label="Carico il registro errori…" />
      ) : q.isError ? (
        <ErrorState onRetry={() => q.refetch()} />
      ) : !q.data?.admin ? (
        <EmptyState
          icon={<ShieldAlert className="size-6" />}
          title="Area riservata"
          description="Questa dashboard è visibile solo agli amministratori interni."
        />
      ) : q.data.reports.length === 0 ? (
        <EmptyState
          icon={<Bug className="size-6" />}
          title="Nessun errore"
          description="Nessuna segnalazione corrisponde ai filtri selezionati."
        />
      ) : (
        <div className="space-y-3">
          {q.data.reports.map((r) => (
            <ReportCard key={r.id} report={r as Report} onFilterCorrelation={setCorrelationId} />
          ))}
        </div>
      )}
    </div>
  );
}

type Report = {
  id: string;
  kind: string;
  severity: string;
  message: string;
  stack: string | null;
  source: string | null;
  commit_sha: string | null;
  branch: string | null;
  run_url: string | null;
  context: unknown;
  created_at: string;
  triage_status: string;
  triage_note: string | null;
  triaged_at: string | null;
  correlation_id: string | null;
  actor_role: string | null;
  build_id: string | null;
  release_channel: string | null;
};

function ReportCard({
  report,
  onFilterCorrelation,
}: {
  report: Report;
  onFilterCorrelation: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = report.kind === "build" ? Hammer : AlertTriangle;
  const queryClient = useQueryClient();
  const triage = useServerFn(updateReportTriage);
  const symbolicate = useServerFn(symbolicateReport);
  const [frames, setFrames] = useState<string[] | null>(null);

  const triageMutation = useMutation({
    mutationFn: (status: "new" | "in_progress" | "resolved") => triage({ data: { id: report.id, status } }),
    onSuccess: () => {
      toast.success("Stato aggiornato");
      void queryClient.invalidateQueries({ queryKey: ["diagnostics"] });
    },
    onError: () => toast.error("Non sono riuscito ad aggiornare lo stato"),
  });

  const symbolicateMutation = useMutation({
    mutationFn: () => symbolicate({ data: { id: report.id } }),
    onSuccess: (res) => {
      if (res.reason === "ok") {
        setFrames(
          res.frames.map((f) =>
            f.original
              ? `${f.original.file}:${f.original.line ?? "?"}:${f.original.column ?? "?"}${f.original.name ? ` (${f.original.name})` : ""}`
              : f.raw.trim(),
          ),
        );
        setOpen(true);
      } else {
        toast.info(
          res.reason === "no-stack"
            ? "Questa segnalazione non ha uno stack trace."
            : res.reason === "no-build-id"
              ? "Manca l'identificativo della build: source map non disponibili."
              : "Nessuna source map caricata per questa build.",
        );
      }
    },
    onError: () => toast.error("Risoluzione dello stack non riuscita"),
  });

  return (
    <Card className="space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <Badge className={severityTone[report.severity] ?? severityTone.error}>{report.severity}</Badge>
        <Badge variant="outline">{report.kind}</Badge>
        <Badge className={statusTone[report.triage_status] ?? statusTone.new}>
          {statusLabel[report.triage_status] ?? report.triage_status}
        </Badge>
        <span className="text-xs text-muted-foreground">{formatDate(report.created_at)}</span>
        {report.branch ? <Badge variant="secondary">{report.branch}</Badge> : null}
        {report.actor_role ? <Badge variant="outline">{report.actor_role}</Badge> : null}
        {report.commit_sha ? (
          <span className="font-mono text-xs text-muted-foreground">{report.commit_sha.slice(0, 7)}</span>
        ) : null}
      </div>

      <p className="break-words text-sm font-medium">{report.message}</p>
      {report.source ? (
        <p className="break-all font-mono text-xs text-muted-foreground">{report.source}</p>
      ) : null}
      {report.correlation_id ? (
        <button
          type="button"
          onClick={() => onFilterCorrelation(report.correlation_id!)}
          className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground underline underline-offset-4"
        >
          <Link2 className="size-3" />
          {report.correlation_id}
          {report.build_id ? ` · build ${report.build_id.slice(0, 7)}` : ""}
        </button>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
          {open ? "Nascondi dettagli" : "Mostra stack e contesto"}
        </Button>
        {report.stack ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => symbolicateMutation.mutate()}
            disabled={symbolicateMutation.isPending}
          >
            <Wand2 className="mr-2 size-4" />
            File originali
          </Button>
        ) : null}
        {report.run_url ? (
          <a className="text-xs underline underline-offset-4" href={report.run_url} target="_blank" rel="noreferrer">
            Apri la run CI
          </a>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          {(["new", "in_progress", "resolved"] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={report.triage_status === s ? "default" : "outline"}
              onClick={() => triageMutation.mutate(s)}
              disabled={triageMutation.isPending}
            >
              {statusLabel[s]}
            </Button>
          ))}
        </div>
      </div>

      {open ? (
        <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-words">
          {frames ? `${frames.join("\n")}\n\n---\n\n` : ""}
          {report.stack ?? "(nessuno stack)"}
          {"\n\n"}
          {JSON.stringify(report.context ?? {}, null, 2)}
        </pre>
      ) : null}
    </Card>
  );
}
